import { useState } from 'react';
import Modal from '@carbon/react/es/components/Modal/Modal';
import { USER_PERSONAS, type UserPersona } from '@ddp/shared';

import { useT, type MessageKey } from '../i18n/index.js';

/**
 * Modale de choix du persona à la première connexion (ADR-0031 §6). Choix **skippable** → repli
 * `GENERALIST` ; jamais un mur bloquant. Le persona est une **préférence d'affichage** (onglet
 * d'accueil + visites proposées), pas un droit — le sous-titre le dit à l'utilisateur.
 *
 * Le focus-trap, l'Échap et l'accessibilité du dialogue viennent du `Modal` de Carbon (NFR-22).
 */
export function PersonaModal({
  onConfirm,
  onSkip,
}: {
  /** Confirme le persona choisi (déclenche l'écriture + l'effet d'accueil côté hôte). */
  onConfirm: (persona: UserPersona) => void;
  /** Passe : repli sur `GENERALIST`, sans redirection ni insistance. */
  onSkip: () => void;
}) {
  const t = useT();
  const [selected, setSelected] = useState<UserPersona>('GENERALIST');

  return (
    <Modal
      open
      size="md"
      modalHeading={t('persona.modal.title')}
      primaryButtonText={t('persona.modal.confirm')}
      secondaryButtonText={t('persona.modal.skip')}
      onRequestSubmit={() => onConfirm(selected)}
      onSecondarySubmit={onSkip}
      onRequestClose={onSkip}
    >
      <p className="ddp-persona__subtitle">{t('persona.modal.subtitle')}</p>
      <div className="ddp-persona__grid" role="radiogroup" aria-label={t('persona.modal.title')}>
        {USER_PERSONAS.map((persona) => {
          const active = persona === selected;
          return (
            <button
              key={persona}
              type="button"
              role="radio"
              aria-checked={active}
              className={`ddp-persona__card${active ? ' ddp-persona__card--active' : ''}`}
              onClick={() => setSelected(persona)}
            >
              <span className="ddp-persona__label">
                {t(`persona.${persona}.label` as MessageKey)}
              </span>
              <span className="ddp-persona__desc">
                {t(`persona.${persona}.desc` as MessageKey)}
              </span>
              <span className="ddp-persona__lands">
                {t(`persona.${persona}.lands` as MessageKey)}
              </span>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
